import { useState } from 'react';
import type { MetricWindow, NodeStatus } from '../api/types';
import { api } from '../api/client';
import { formatGb } from '../format';
import { usePoll } from '../hooks/usePoll';
import { LineChart } from './LineChart';
import { MetricWindowPicker, windowPollMs } from './MetricWindowPicker';
import { Empty, ErrorAlert } from './ui';

/** ダッシュボードのノードの推移。値は Proxmox のノードの RRD（Panel には保存しない） */
export function NodeTrends({ nodes }: { nodes: NodeStatus[] }) {
  const [period, setPeriod] = useState<MetricWindow>('1h');
  const online = nodes.filter((node) => node.online);
  const names = online.map((node) => node.name).join(',');
  const metrics = usePoll(
    () => Promise.all(online.map(async (node) => ({ node, data: await api.nodeMetrics(node.name, period) }))),
    windowPollMs(period),
    [names, JSON.stringify(period)],
  );
  const zoom = (from: string, to: string) => setPeriod({ from, to });
  return (
    <section className="stack" style={{ gap: 10 }}>
      <h3>ノードの推移</h3>
      <MetricWindowPicker value={period} onChange={setPeriod} presets={false} />
      <ErrorAlert error={metrics.error} />
      {online.length === 0 && <Empty>動いているノードがありません。</Empty>}
      <div className="grid grid-charts">
        {(metrics.data ?? []).map(({ node, data }) => {
          const times = data.points.map((p) => p.t);
          return (
            <div key={node.name} className="card card-body stack" style={{ gap: 8 }}>
              <h3>{node.name}</h3>
              <span className="small muted">CPU</span>
              <LineChart
                times={times}
                series={[{ label: 'CPU', values: data.points.map((p) => p.cpu), color: 'var(--chart-1)' }]}
                min={0}
                max={1}
                format={(v) => `${Math.round(v * 100)}%`}
                onSelect={zoom}
              />
              <span className="small muted">メモリ（使用中）</span>
              <LineChart
                times={times}
                series={[{ label: 'メモリ', values: data.points.map((p) => p.memoryMb), color: 'var(--chart-2)' }]}
                min={0}
                max={node.memoryTotalMb}
                format={(v) => formatGb(v)}
                onSelect={zoom}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
